import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HiOutlineDuplicate } from 'react-icons/hi';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { deleteRole, getPermissions, getRoles } from 'api/rolesApi';
import NoResult from 'components/common/noResult';
import RoleModal from 'components/modal/roleModal/roleModal';
import SpinnerComponent from 'components/spinner';
import ActionTable from 'components/table/actionTable';
import PaginateTable from 'components/table/paginate';
import Sort from 'components/table/sort';
import { Table } from 'flowbite-react';
import { size } from 'lodash';
import { orgSelector, userSelector } from 'redux/selectors';
import { defaultpageCount, exportFileName, permissionKeys, sortByTypeType } from 'utils/constants';
import { roleType } from 'utils/proptypes';
import { checkPermission, copyToClipboard, exportToFile } from 'utils/utils';

import Actions from '../components/actions';

import './roleManagement.scss';
import '../styles/styles.scss';

const initRole = {
  id: '',
  displayName: '',
  type: '',
  permissions: [],
};

const RoleManagementPage = (props: any) => {
  const { WRITE_ROLE } = permissionKeys;
  const [t] = useTranslation();
  const { organizationId } = useSelector(orgSelector);
  const { userInfo } = useSelector(userSelector);
  const { userRole } = userInfo;
  const [searchValue, setSearchValue] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [totalEntities, setTotalEntities] = useState(0);
  const [roles, setRoles] = useState([]);
  const [targetRole, setTargetRole] = useState(initRole);
  const [openModal, setOpenModal] = useState(false);
  const [permissions, setPermission] = useState([]);
  const queryClient = useQueryClient();

  const [sortBy, setSortBy] = useState('');
  const [sortByType, setSortByType] = useState('');
  const isWriteOrg = checkPermission(userInfo, props.type, [WRITE_ROLE], organizationId);
  const [pageCount, setPageCount] = useState(defaultpageCount);

  const getAllPermission = useMutation(['getAllPermission'], {
    mutationFn: getPermissions,
    onSuccess: ({ data }) => setPermission(data),
    onError: () => setPermission([]),
  });

  const { data, isLoading, error } = useQuery(
    ['getRoles', currentPage, searchValue, sortBy, sortByType,pageCount],
    () => getRoles({ page: currentPage, limit: pageCount, searchQuery: searchValue, sortBy, sortByType }),
    {
      staleTime: Infinity,
    },
  );

  const handleSearch = (value: any) => {
    setSearchValue(value);
    setCurrentPage(0);
  };

  useEffect(() => {
    getAllPermission.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (data) {
      setRoles(data.data?.entities || []);
      setTotalEntities(data.data?.totalEntities || 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    setTotalEntities(data?.data.totalEntities);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  const handleClickEdit = (item: any) => {
    setTargetRole(item);
    setOpenModal(true);
  };

  const handleClickAddRole = () => {
    setTargetRole(initRole);
    setOpenModal(true);
  };

  const handleCloseModal = (isCloseModal: any) => {
    setOpenModal(isCloseModal);
    if (!isCloseModal) {
      setTargetRole(initRole);
    }
  };

  const handleClickDelete = async (id: string) => {
    try {
      const response = await deleteRole(id);
      if (response.status === 200) {
        await queryClient.invalidateQueries(['getRoles']);
        const message: string = t('roleManagementPage.deleteSuccessfully')
        toast.success(message)
      }
    } catch (error: any) {
      const message: string = `${error.response.data.errors?.[0].detail}`;
      toast.error(message);
    }
  };

  const handleHeaderSort = (field: string) => {
    setSortBy(field);
    let type = '';
    if (field !== sortBy) {
      type = sortByTypeType.ASC
    } else if (sortByType === sortByTypeType.ASC) {
      type = sortByTypeType.DESC;
    } else {
      setSortBy(type);
    }
    setSortByType(type)
  }

  const handleOnClickExport = () => {
    const exportedData: any = roles.map((item: any) => ({
      Name: item.displayName,
      Type: item.type,
      Permissions: item?.permissions
        ?.map((itemPremission: any) => {
          return itemPremission;
        })
        .join(', '),
    }));
    exportToFile(exportedData, exportFileName.ROLE);
  };

  useEffect(() => {
    if (error) {
      setRoles([])
    }
  }, [error]);

  return (
    <div className="w-full role-management">
      <div>
        <ActionTable
          placeholderSearch={t('roleManagementPage.placeholderSearch')}
          buttonName={t('roleManagementPage.buttonAddRoleLabel')}
          handleAddClick={checkPermission(userInfo, props.type, [WRITE_ROLE], organizationId) && handleClickAddRole}
          handleSearch={handleSearch}
        />
      </div>
      {totalEntities === 0 && !isLoading && <NoResult />}
      {totalEntities > 0 && (
        <div className="bg-white">
          <Table className="border-b">
            <Table.Head className="text-sm text-gray-600 font-semibold">
              <Table.HeadCell className="bg-gray-100 font-semibold" onClick={() => handleHeaderSort('displayName')}>
                <div className="flex items-center">
                  {t('roleManagementPage.Table.ColumnColName')}
                  <Sort check={sortBy === 'displayName'} sortByType={sortByType} />
                </div>
              </Table.HeadCell>
              <Table.HeadCell className="bg-gray-100 hidden-mobile font-semibold" onClick={() => handleHeaderSort('type')}>
                <div className="flex items-center">
                  {t('roleManagementPage.Table.ColumnColType')}
                  <Sort check={sortBy === 'type'} sortByType={sortByType} />
                </div>
              </Table.HeadCell>
              <Table.HeadCell className="bg-gray-100 hidden-mobile font-semibold " onClick={() => handleHeaderSort('countPermission')}>
                <div className="flex items-center">
                  {t('roleManagementPage.Table.countPermission')}
                  <Sort check={sortBy === 'countPermission'} sortByType={sortByType} />
                </div>
              </Table.HeadCell>
              <Table.HeadCell className="bg-gray-100 hidden-mobile-tablet font-semibold " onClick={() => handleHeaderSort('description')}>
                <div className="flex items-center">
                  {t('roleManagementPage.Table.description')}
                  <Sort check={sortBy === 'description'} sortByType={sortByType} />
                </div>
              </Table.HeadCell>
              <Table.HeadCell className="bg-gray-100 w-6">{''}</Table.HeadCell>
            </Table.Head>
            <Table.Body>
              {roles?.map((item: roleType, index) => (
                <Table.Row key={`row-${index + 1}`} className="bg-white text-base hover:bg-gray-100 border-b">
                  <Table.Cell className="font-normal text-gray-900 py-2.5">
                    <div className="flex flex-row items-center">
                      <div>{`${item.displayName || ''}`}</div>
                      <div className="copy-icon pl-3 cursor-pointer">
                        <HiOutlineDuplicate size={25} onClick={() => copyToClipboard(item.displayName, t('roleManagementPage.copiedName'))} />
                      </div>
                    </div>
                  </Table.Cell>
                  <Table.Cell className="font-normal text-gray-900 py-2.5 hidden-mobile">{item.type}</Table.Cell>
                  <Table.Cell className="font-normal text-gray-900 py-2.5 hidden-mobile">{size(item.permissions)}</Table.Cell>
                  <Table.Cell className="font-normal text-gray-900 py-2.5 hidden-mobile-tablet">{item.description}</Table.Cell>
                  <Table.Cell className="py-2.5">
                    {isWriteOrg && (
                      <Actions>
                        <Actions.Item action={() => handleClickEdit(item)} label={t('edit')} />
                        <Actions.Item action={() => handleClickDelete(item.id)} label={t('roleManagementPage.buttonDeleteRoleLabel')} />
                      </Actions>
                    )}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
          {isLoading && <SpinnerComponent />}
          <PaginateTable
            setCurrentPage={setCurrentPage}
            currentPage={currentPage}
            totalEntities={totalEntities}
            isLoadingTable={isLoading}
            exportHandler={handleOnClickExport}
            pageCount={pageCount}
            setPageCount={setPageCount}
          />
        </div>
      )}
      {openModal && (
        <RoleModal
          headerTitle={targetRole.id.length > 0 ? t('roleManagementPage.RoleModal.ModalEditTitle') : t('roleManagementPage.RoleModal.ModalAddTitle')}
          openModal={openModal}
          targetData={targetRole}
          isEdit={targetRole.id.length > 0}
          allAvailablePermission={permissions || []}
          handleCloseModal={(isClose: any) => handleCloseModal(isClose)}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          permissions={permissions}
        />
      )}
    </div>
  );
};

export default RoleManagementPage;
